import React from 'react';
import classNames from 'classnames';

import makeLogger from 'lib/makeLogger';
import Tooltip from '../tooltip/Tooltip';
import { ReactComponent as ClipboardOutline } from 'assets/images/clipboard-outline.svg';

import './CopyButton.scss';

const logErr = makeLogger('lib/components/copy-button', console.error);

const COPIED_RESET_DELAY = 1500;

export const DEFAULT_LABEL = 'Copy to clipboard';
export const COPIED_LABEL = 'Copied!';

/**
 * Copy through a hidden textarea, the only route left where the async
 * Clipboard API is missing or refused.
 * @param {string} text value to copy
 */
function copyWithTextarea(text) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    if (!document.execCommand('copy')) {
      throw new Error('execCommand("copy") was rejected');
    }
  } finally {
    document.body.removeChild(textarea);
  }
}

/**
 * Write text to the clipboard. The async Clipboard API is missing outside
 * secure contexts and denied outright by some embedded browsers, so a refusal
 * has to fall back rather than surface as a failed copy.
 * @param {string} text value to copy
 */
export async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      logErr('clipboard API refused, falling back to execCommand', error);
    }
  }

  copyWithTextarea(text);
}

/**
 * Small clipboard icon that copies `value` when clicked. Renders nothing when
 * there is no value to copy.
 */
const CopyButton = ({ value, label = DEFAULT_LABEL, className, ...props }) => {
  const [copied, setCopied] = React.useState(false);
  const resetTimeout = React.useRef(null);

  React.useEffect(() => () => clearTimeout(resetTimeout.current), []);

  if (!value) {
    return null;
  }

  const handleClick = async (event) => {
    // The button often sits inside a linked card, where a bubbling click would
    // navigate away instead of copying.
    event.preventDefault();
    event.stopPropagation();

    try {
      await copyText(value);
      setCopied(true);
      clearTimeout(resetTimeout.current);
      resetTimeout.current = setTimeout(
        () => setCopied(false),
        COPIED_RESET_DELAY,
      );
    } catch (error) {
      logErr('failed to copy to clipboard', error);
    }
  };

  return (
    <Tooltip
      className={classNames('copy-button', className)}
      overlay={copied ? COPIED_LABEL : label}
      placement='top'
      // Tippy hides on click by default, which would swallow the confirmation.
      hideOnClick={false}
    >
      <button
        type='button'
        className='copy-button-control'
        aria-label={label}
        data-testid='CopyButton.button'
        onClick={handleClick}
        {...props}
      >
        <ClipboardOutline />
      </button>
    </Tooltip>
  );
};

export default CopyButton;
